import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import useNotifications from "../../hooks/useNotifications";
import NotificationPanel from "./NotificationPanel";

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [ref, onOutside]);
}

/**
 * Header bell + unread badge + dropdown panel.
 * Replaces the mock notification UI in DashboardHeader.
 */
export default function NotificationBell({
  notificationsPath,
  isPortal = false,
}) {
  const navigate = useNavigate();
  const { unreadCount, active } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, () => setOpen(false));

  const resolvedPath =
    notificationsPath ||
    (isPortal ? "/staff-panel/notifications" : "/notifications");

  const handleViewAll = () => {
    setOpen(false);
    navigate(resolvedPath);
  };

  // Student / inactive: still show a quiet bell (no API)
  if (!active) {
    return (
      <button
        type="button"
        className="relative inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-primary-700 dark:hover:text-primary-400"
        aria-label="Notifications"
        onClick={() => navigate(resolvedPath)}
      >
        <Bell size={18} />
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-primary-700 dark:hover:text-primary-400"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-500 px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-white dark:ring-neutral-900">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationPanel
          onClose={() => setOpen(false)}
          onViewAll={handleViewAll}
        />
      )}
    </div>
  );
}
